import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, keepPreviousData, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Search, ChevronUp, ChevronDown, Lock, LockOpen, CalendarClock } from "lucide-react";
import { ConfirmDialog } from "../components/shared/ConfirmDialog";
import { PageHeader } from "../components/shared/PageHeader";
import { DataTable } from "../components/shared/DataTable";
import { Pagination } from "../components/shared/Pagination";
import { StatusBadge } from "../components/shared/StatusBadge";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Button } from "../components/ui/button";
import { SortControl } from "../components/shared/SortControl";
import { SubscriptionFormDialog } from "../components/subscriptions/SubscriptionFormDialog";
import { apiClient, getApiErrorMessage } from "../lib/apiClient";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { useListState } from "../hooks/useListState";
import { formatDate, todayLocal } from "../lib/utils";
import { paymentStateMeta } from "../lib/statusMaps";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";

const PAGE_SIZE = 20;
const QUICK_FILTERS = [
  { value: "all", label: "All" },
  { value: "active", label: "Active" },
  { value: "unpaid", label: "Unpaid" },
  { value: "pending", label: "Pending" },
  { value: "deactivated", label: "Deactivated" },
  { value: "expired", label: "Expired" },
  { value: "starting_today", label: "Starting Today" },
  { value: "future_deliveries", label: "Future Deliveries" },
  { value: "paused", label: "Paused" },
  { value: "deliveries_today", label: "Today's Deliveries" },
  { value: "pickup", label: "Pickup" },
];
const PAUSED_ARRANGE = [
  { value: "date", label: "By Date" }, { value: "customer", label: "By Customer" },
  { value: "today", label: "Today's Paused" }, { value: "notselected", label: "Day not Selected" },
];
const PICKUP_ARRANGE = [
  { value: "today", label: "Today's Pickups" }, { value: "all", label: "All Pickups" },
];
// Sub-filters under the "Pending" master filter (online checkout drafts).
const PENDING_SUB = [
  { value: "all", label: "All Pending" },
  { value: "awaiting", label: "Awaiting" },
  { value: "abandoned", label: "Abandoned" },
];
const SORT_FIELDS = [
  { value: "id", label: "Newest Created" }, { value: "start_date", label: "Start Date" }, { value: "end_date", label: "End Date" },
];

function SortableDateHeader({ label, field, sortBy, sortDir, onSort }) {
  const active = sortBy === field;
  return (
    <button
      type="button"
      className="inline-flex items-center gap-1 hover:text-text-main"
      onClick={() => onSort(field)}
      data-testid={`subscription-header-sort-${field}`}
    >
      {label}
      <span className="flex flex-col leading-none">
        <ChevronUp size={10} strokeWidth={2.5} className={active && sortDir === "asc" ? "text-primary" : "text-text-muted/40"} />
        <ChevronDown size={10} strokeWidth={2.5} className={active && sortDir === "desc" ? "text-primary" : "text-text-muted/40"} />
      </span>
    </button>
  );
}

export default function Subscriptions() {
  const navigate = useNavigate();
  const [search, setSearch] = useListState("subscriptions.search", "");
  const debouncedSearch = useDebouncedValue(search, 400);
  const [quickFilter, setQuickFilter] = useListState("subscriptions.quickFilter", "all");
  const [sortBy, setSortBy] = useListState("subscriptions.sortBy", "id");
  const [sortDir, setSortDir] = useListState("subscriptions.sortDir", "desc");
  const [includeDeleted, setIncludeDeleted] = useListState("subscriptions.includeDeleted", false);
  const [pausedArrange, setPausedArrange] = useListState("subscriptions.pausedArrange", "date");
  const [pickupArrange, setPickupArrange] = useListState("subscriptions.pickupArrange", "today");
  const [pendingSub, setPendingSub] = useListState("subscriptions.pendingSub", "all");
  const [deliveryDate, setDeliveryDate] = useListState("subscriptions.deliveryDate", todayLocal());
  const [page, setPage] = useListState("subscriptions.page", 0);
  const [formOpen, setFormOpen] = useState(false);
  const [expireOpen, setExpireOpen] = useState(false);
  const [expirePreview, setExpirePreview] = useState({ loading: false, count: null });
  const [expireRunning, setExpireRunning] = useState(false);
  const queryClient = useQueryClient();

  const countsQuery = useQuery({
    queryKey: ["subscription-counts", { includeDeleted }],
    queryFn: async () => (await apiClient.get("/subscriptions/counts", { params: { include_deleted: includeDeleted } })).data,
  });
  const counts = countsQuery.data ?? null;

  const pausedCountsQuery = useQuery({
    queryKey: ["subscription-paused-counts"],
    queryFn: async () => {
      const [pausedToday, pausedCurrentFuture, notSelected] = await Promise.all([
        apiClient.get("/deliveries/paused-tiffins", { params: { scope: "today", limit: 1 } }),
        apiClient.get("/deliveries/paused-tiffins", { params: { scope: "all", time_filter: "future", exclude_past: true, limit: 1 } }),
        apiClient.get("/deliveries/day-not-selected", { params: { limit: 1 } }),
      ]);
      return { today: pausedToday.data.total, currentFuture: pausedCurrentFuture.data.total, notselected: notSelected.data.total };
    },
  });
  const pausedCounts = pausedCountsQuery.data ?? { today: null, currentFuture: null, notselected: null };

  const deliveriesTodayCountQuery = useQuery({
    queryKey: ["subscription-deliveries-today-count"],
    queryFn: async () => (await apiClient.get("/subscriptions", { params: { delivery_date: todayLocal(), type: 2, limit: 1 } })).data.total,
  });
  const deliveriesTodayCount = deliveriesTodayCountQuery.data ?? null;

  const futureCountQuery = useQuery({
    queryKey: ["subscription-future-count"],
    queryFn: async () => (await apiClient.get("/subscriptions", { params: { future: true, limit: 1 } })).data.total,
  });
  const futureDeliveriesCount = futureCountQuery.data ?? null;

  const pickupCountsQuery = useQuery({
    queryKey: ["subscription-pickup-counts"],
    queryFn: async () => {
      const [today, all] = await Promise.all([
        apiClient.get("/subscriptions", { params: { delivery_date: todayLocal(), type: 1, limit: 1 } }),
        apiClient.get("/subscriptions", { params: { status: "1", type: 1, limit: 1 } }),
      ]);
      return { today: today.data.total, all: all.data.total };
    },
  });
  const pickupCounts = pickupCountsQuery.data ?? { today: null, all: null };

  // finalize-status gates the critical Finalize/Reopen actions -> always fresh
  // (staleTime 0), and only fetched when the "Today's Deliveries" view is open.
  const finalizeStatusQuery = useQuery({
    queryKey: ["finalize-status", deliveryDate],
    queryFn: async () => (await apiClient.get("/deliveries/finalize-status", { params: { date: deliveryDate } })).data,
    enabled: quickFilter === "deliveries_today" && !!deliveryDate,
    staleTime: 0,
  });
  const finalizeStatus = finalizeStatusQuery.data ?? null;

  const listQuery = useQuery({
    queryKey: ["subscriptions-list", {
      q: debouncedSearch, quickFilter, sortBy, sortDir, includeDeleted,
      pausedArrange, pickupArrange, pendingSub, deliveryDate, page,
    }],
    queryFn: async () => {
      if (quickFilter === "paused") {
        let items = [];
        if (pausedArrange === "notselected") {
          const { data } = await apiClient.get("/deliveries/day-not-selected", {
            params: { q: debouncedSearch || undefined, limit: 500 },
          });
          items = data.items || [];
        } else {
          const todayMode = pausedArrange === "today";
          const { data } = await apiClient.get("/deliveries/paused-tiffins", {
            params: todayMode
              ? { scope: "today", q: debouncedSearch || undefined, limit: 500 }
              : { scope: "all", time_filter: "both", exclude_past: true, q: debouncedSearch || undefined, limit: 500 },
          });
          items = data.items || [];
          items.sort((a, b) =>
            pausedArrange === "customer" || todayMode
              ? (a.customer_name || `#${a.customer_id}`).localeCompare(b.customer_name || `#${b.customer_id}`)
              : (a.pause_from || "").localeCompare(b.pause_from || ""),
          );
        }
        const paged = items
          .slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE)
          .map((it) => ({ ...it, id: it.subscription_id }));
        return { total: items.length, items: paged };
      }
      const params = {
        q: debouncedSearch || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        include_deleted: includeDeleted,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (quickFilter === "active") params.status = "1";
      else if (quickFilter === "deactivated") params.status = "3";
      else if (quickFilter === "expired") params.status = "4";
      else if (quickFilter === "unpaid") params.payment_state = "unpaid";
      else if (quickFilter === "pending") params.payment_state = pendingSub === "all" ? "pending" : pendingSub;
      else if (quickFilter === "starting_today") params.start_date = todayLocal();
      else if (quickFilter === "future_deliveries") params.future = true;
      else if (quickFilter === "deliveries_today") { params.delivery_date = deliveryDate; params.type = 2; }
      else if (quickFilter === "pickup") {
        params.type = 1;
        if (pickupArrange === "today") params.delivery_date = deliveryDate;
        else params.status = "1";
      }
      const { data } = await apiClient.get("/subscriptions", { params });
      return { total: data.total, items: data.items };
    },
    placeholderData: keepPreviousData,
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const listError = listQuery.isError ? getApiErrorMessage(listQuery.error, "Could not load subscriptions.") : null;

  // Reset to the first page whenever a filter/search changes (page excluded
  // from this key so paging itself doesn't reset).
  const filterKey = `${debouncedSearch}|${quickFilter}|${sortBy}|${sortDir}|${includeDeleted}|${pausedArrange}|${pickupArrange}|${pendingSub}|${deliveryDate}`;
  const prevFilterKeyRef = useRef(filterKey);
  useEffect(() => {
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      if (page !== 0) setPage(0);
    }
  }, [filterKey, page, setPage]);

  const invalidateDeliveryViews = () => {
    queryClient.invalidateQueries({ queryKey: ["finalize-status"] });
    queryClient.invalidateQueries({ queryKey: ["subscription-deliveries-today-count"] });
    queryClient.invalidateQueries({ queryKey: ["subscriptions-list"] });
  };

  // Daily expiry sweep: expire ACTIVE subs that are due (end_date <= today) AND
  // have received all their tiffins. Preview the count, confirm, then apply.
  const openExpireSweep = async () => {
    setExpireOpen(true);
    setExpirePreview({ loading: true, count: null });
    try {
      const { data } = await apiClient.get("/subscriptions/expire-preview", {
        params: { end_date_lte: todayLocal() },
      });
      setExpirePreview({ loading: false, count: data.total });
    } catch (error) {
      setExpirePreview({ loading: false, count: null });
      toast.error(getApiErrorMessage(error, "Could not check which subscriptions are due to expire."));
    }
  };

  const runExpireSweep = async () => {
    setExpireRunning(true);
    try {
      const { data } = await apiClient.post("/subscriptions/expire/run-daily");
      toast.success(
        data.expired_count
          ? `Expired ${data.expired_count} subscription${data.expired_count === 1 ? "" : "s"} that finished their tiffins.`
          : "No subscriptions were due to expire.",
      );
      setExpireOpen(false);
      queryClient.invalidateQueries({ queryKey: ["subscription-counts"] });
      queryClient.invalidateQueries({ queryKey: ["subscriptions-list"] });
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not run the expiry sweep."));
    } finally {
      setExpireRunning(false);
    }
  };

  // Finalize / reopen stay server-validated (the POST is authoritative);
  // useMutation only drives the button state and post-success invalidation.
  const finalizeMutation = useMutation({
    mutationFn: (date) => apiClient.post("/deliveries/finalize", null, { params: { date } }).then((r) => r.data),
    onSuccess: (data) => {
      toast.success(`Finalized — ${data.created} new delivery record(s). Route-making is now unlocked.`);
      invalidateDeliveryViews();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not finalize today's deliveries.")),
  });

  const reopenMutation = useMutation({
    mutationFn: (date) => apiClient.post("/deliveries/reopen", null, { params: { date } }),
    onSuccess: () => {
      toast.success("Reopened — today's deliveries are editable again.");
      invalidateDeliveryViews();
    },
    onError: (e) => toast.error(getApiErrorMessage(e, "Could not reopen today's deliveries.")),
  });
  const finalizing = finalizeMutation.isPending || reopenMutation.isPending;

  const handleHeaderSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const columns = [
    { key: "customer_name", header: "Customer", render: (r) => r.customer_name || `#${r.customer_id}` },
    {
      key: "customer_address", header: "Address",
      render: (r) => (
        <span className="block max-w-[14rem] truncate text-text-muted" title={r.customer_address || ""}>
          {r.customer_address || "—"}
        </span>
      ),
    },
    { key: "customer_phone", header: "Phone", render: (r) => r.customer_phone || "—" },
    { key: "plan_name", header: "Plan", subtitle: true, render: (r) => r.plan_name || `#${r.plan_id}` },
    {
      key: "start_date",
      header: <SortableDateHeader label="Start" field="start_date" sortBy={sortBy} sortDir={sortDir} onSort={handleHeaderSort} />,
      render: (r) => formatDate(r.start_date),
    },
    {
      key: "end_date",
      header: <SortableDateHeader label="End" field="end_date" sortBy={sortBy} sortDir={sortDir} onSort={handleHeaderSort} />,
      render: (r) => formatDate(r.end_date),
    },
    {
      key: "internal_notes", header: "Internal Notes",
      render: (r) =>
        r.internal_notes ? (
          <TooltipProvider delayDuration={120}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block max-w-[14rem] cursor-default truncate text-text-muted" data-testid={`subscription-internal-notes-${r.id}`}>
                  {r.internal_notes}
                </span>
              </TooltipTrigger>
              <TooltipContent data-testid={`subscription-internal-notes-tooltip-${r.id}`}>
                {r.internal_notes}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <span className="text-text-muted">—</span>
        ),
    },
    {
      key: "payment_state", header: "Payment",
      render: (r) => {
        const m = paymentStateMeta(r.payment_state);
        return <StatusBadge label={m.label} variant={m.variant} data-testid={`subscription-payment-badge-${r.id}`} />;
      },
    },
  ];

  const pausedColumns = [
    { key: "customer_name", header: "Customer", render: (r) => r.customer_name || `#${r.customer_id}` },
    { key: "phone", header: "Phone", render: (r) => r.phone || "—" },
    { key: "plan_name", header: "Plan", subtitle: true, render: (r) => r.plan_name || "—" },
    { key: "pause_from", header: "Pause From", render: (r) => formatDate(r.pause_from) },
    { key: "pause_to", header: "Pause To", render: (r) => formatDate(r.pause_to) },
    {
      key: "status_label", header: "Status",
      render: (r) => {
        if (!r.pause_from) {
          return <StatusBadge label="Not scheduled today" variant="default" data-testid={`subscription-pause-status-${r.id}`} />;
        }
        const active = r.status_label === "active";
        return <StatusBadge label={active ? "Active pause" : "Upcoming"} variant={active ? "warning" : "default"} data-testid={`subscription-pause-status-${r.id}`} />;
      },
    },
  ];

  const isPaused = quickFilter === "paused";
  const isDeliveriesToday = quickFilter === "deliveries_today";
  const isPickup = quickFilter === "pickup";
  const isPending = quickFilter === "pending";
  const activeColumns = isPaused ? pausedColumns : columns;
  const pausedChipCount = pausedCounts.currentFuture != null && pausedCounts.notselected != null
    ? pausedCounts.currentFuture + pausedCounts.notselected
    : null;
  const chipCountFor = (value) =>
    value === "paused" ? pausedChipCount
      : value === "deliveries_today" ? deliveriesTodayCount
        : value === "future_deliveries" ? futureDeliveriesCount
          : value === "pickup" ? pickupCounts.all
            : counts?.[value];

  return (
    <div data-testid="subscriptions-page">
      <PageHeader
        title="Subscriptions"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={openExpireSweep} data-testid="subscription-expire-sweep-button">
              <CalendarClock size={16} strokeWidth={2} /> Expire Delivered Plans
            </Button>
            <Button variant="accent" onClick={() => setFormOpen(true)} data-testid="subscription-create-button">
              <Plus size={16} strokeWidth={2} /> New Subscription
            </Button>
          </div>
        }
      />

      <div className="mb-4 flex items-center gap-3">
        <div className="flex w-full flex-nowrap items-center gap-1.5" data-testid="subscription-quick-filter">
          {QUICK_FILTERS.map((f) => (
            <Button
              key={f.value}
              type="button"
              variant={quickFilter === f.value ? "default" : "outline"}
              onClick={() => setQuickFilter(f.value)}
              className="h-9 shrink-0 whitespace-nowrap px-2.5 text-xs font-semibold"
              data-testid={`subscription-quick-filter-${f.value}`}
            >
              {f.label}
              {chipCountFor(f.value) != null && (
                <span
                  className={`ml-1.5 rounded-full px-1.5 py-0.5 text-[11px] font-semibold ${quickFilter === f.value ? "bg-white/20 text-white" : "bg-background text-text-muted"}`}
                  data-testid={`subscription-quick-filter-count-${f.value}`}
                >
                  {chipCountFor(f.value).toLocaleString()}
                </span>
              )}
            </Button>
          ))}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        {isPending && (
          <div className="flex items-center gap-2" data-testid="subscription-pending-sub">
            <span className="text-sm text-text-muted">Show:</span>
            {PENDING_SUB.map((a) => (
              <Button
                key={a.value}
                type="button"
                size="sm"
                variant={pendingSub === a.value ? "default" : "outline"}
                onClick={() => setPendingSub(a.value)}
                className="h-9 shrink-0 whitespace-nowrap px-2.5 text-xs font-semibold"
                data-testid={`subscription-pending-sub-${a.value}`}
              >
                {a.label}
              </Button>
            ))}
          </div>
        )}
        <label className="order-last ml-auto flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap text-sm text-text-muted" data-testid="subscription-show-deleted-toggle">
          <Checkbox
            checked={includeDeleted}
            onCheckedChange={(v) => setIncludeDeleted(Boolean(v))}
            data-testid="subscription-show-deleted-checkbox"
          />
          Show deleted
        </label>
        <div className="relative w-full max-w-xs">
          <Search size={15} strokeWidth={1.5} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <Input
            className="pl-9"
            placeholder="Search customer name, phone, address..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="subscription-search-input"
          />
        </div>
        {!isPaused && (
          <SortControl
            fields={SORT_FIELDS} sortBy={sortBy} onSortByChange={setSortBy} sortDir={sortDir} onSortDirChange={setSortDir}
            testId="subscription-sort"
          />
        )}
        {isPaused && (
          <div className="flex items-center gap-2" data-testid="subscription-paused-arrange">
            <span className="text-sm text-text-muted">Arrange:</span>
            {PAUSED_ARRANGE.map((a) => {
              const badge = a.value === "today" ? pausedCounts.today
                : a.value === "notselected" ? pausedCounts.notselected : null;
              return (
                <Button
                  key={a.value}
                  type="button"
                  size="sm"
                  variant={pausedArrange === a.value ? "default" : "outline"}
                  onClick={() => setPausedArrange(a.value)}
                  data-testid={`subscription-paused-arrange-${a.value}`}
                >
                  {a.label}
                  {badge != null && (
                    <span
                      className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${pausedArrange === a.value ? "bg-white/20 text-white" : "bg-background text-text-muted"}`}
                      data-testid={`subscription-paused-arrange-count-${a.value}`}
                    >
                      {badge.toLocaleString()}
                    </span>
                  )}
                </Button>
              );
            })}
          </div>
        )}
        {isPickup && (
          <div className="flex items-center gap-2" data-testid="subscription-pickup-arrange">
            <span className="text-sm text-text-muted">View:</span>
            {PICKUP_ARRANGE.map((a) => {
              const badge = a.value === "today" ? pickupCounts.today
                : a.value === "all" ? pickupCounts.all : null;
              return (
                <Button
                  key={a.value}
                  type="button"
                  size="sm"
                  variant={pickupArrange === a.value ? "default" : "outline"}
                  onClick={() => setPickupArrange(a.value)}
                  data-testid={`subscription-pickup-arrange-${a.value}`}
                >
                  {a.label}
                  {badge != null && (
                    <span
                      className={`ml-2 rounded-full px-2 py-0.5 text-xs font-semibold ${pickupArrange === a.value ? "bg-white/20 text-white" : "bg-background text-text-muted"}`}
                      data-testid={`subscription-pickup-arrange-count-${a.value}`}
                    >
                      {badge.toLocaleString()}
                    </span>
                  )}
                </Button>
              );
            })}
          </div>
        )}
        {(isDeliveriesToday || (isPickup && pickupArrange === "today")) && (
          <div className="flex items-center gap-2" data-testid="subscription-delivery-date">
            <span className="text-sm text-text-muted">Date:</span>
            <Input
              type="date"
              className="w-44"
              value={deliveryDate}
              onChange={(e) => setDeliveryDate(e.target.value || todayLocal())}
              data-testid="subscription-delivery-date-input"
            />
            {deliveryDate !== todayLocal() && (
              <Button type="button" size="sm" variant="outline" onClick={() => setDeliveryDate(todayLocal())} data-testid="subscription-delivery-date-today">
                Today
              </Button>
            )}
          </div>
        )}
      </div>

      {isDeliveriesToday && finalizeStatus && (
        <div
          className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3 text-sm ${finalizeStatus.finalized ? "border-success/30 bg-success-bg text-text-main" : "border-warning/30 bg-warning-bg text-text-main"}`}
          data-testid="deliveries-finalize-bar"
        >
          {finalizeStatus.finalized ? (
            <>
              <span className="flex items-center gap-2 font-medium">
                <Lock size={16} strokeWidth={2} />
                Finalized &amp; locked — route-making is unlocked
                {finalizeStatus.route_count > 0 && ` · ${finalizeStatus.route_count} route(s) today`}
              </span>
              <div className="flex items-center gap-2">
                {!finalizeStatus.can_reopen && (
                  <span className="text-xs text-text-muted">Delete today's routes to reopen</span>
                )}
                <Button
                  type="button" size="sm" variant="outline"
                  disabled={!finalizeStatus.can_reopen || finalizing}
                  onClick={() => reopenMutation.mutate(deliveryDate)}
                  data-testid="deliveries-reopen-button"                >
                  <LockOpen size={15} strokeWidth={2} /> Reopen
                </Button>
              </div>
            </>
          ) : deliveryDate === todayLocal() ? (
            <>
              <span className="font-medium">Live preview — finalize to lock today and enable route-making.</span>
              <Button
                type="button" size="sm" variant="accent"
                disabled={finalizing}
                onClick={() => finalizeMutation.mutate(deliveryDate)}
                data-testid="deliveries-finalize-button"
              >
                <Lock size={15} strokeWidth={2} /> {finalizing ? "Finalizing..." : "Finalize Today's Deliveries"}
              </Button>
            </>
          ) : (
            <span className="text-text-muted">Finalize is only available for today's deliveries.</span>
          )}
        </div>
      )}

      <DataTable
        testId="subscriptions-table"
        columns={activeColumns}
        rows={items}
        loading={listQuery.isPending}
        error={listError}
        onRetry={() => listQuery.refetch()}
        onRowClick={(row) => navigate(`/subscriptions/${row.id}`)}
        emptyTitle={isPaused ? "No paused subscriptions" : isDeliveriesToday ? "No deliveries for this date" : isPickup ? "No pickups found" : "No subscriptions found"}
        emptyDescription={isPaused ? "No tiffins are paused for today or any upcoming date." : isDeliveriesToday ? "No subscriptions have a delivery scheduled on the selected date." : isPickup ? (pickupArrange === "today" ? "No active pickups are scheduled for the selected date." : "There are no active pickup subscriptions.") : "Try adjusting your filters, or create a new subscription."}
      />
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} testId="subscriptions-pagination" />

      <SubscriptionFormDialog open={formOpen} onOpenChange={setFormOpen} onSaved={() => {
        setFormOpen(false);
        queryClient.invalidateQueries({ queryKey: ["subscriptions-list"] });
        queryClient.invalidateQueries({ queryKey: ["subscription-counts"] });
      }} />

      <ConfirmDialog
        open={expireOpen}
        onOpenChange={setExpireOpen}
        title="Expire delivered plans"
        description={
          expirePreview.loading
            ? "Checking which subscriptions have finished all their tiffins…"
            : expirePreview.count === null
              ? "Could not load the count. You can still run the sweep."
              : expirePreview.count === 0
                ? "No active subscriptions are due to expire right now. Running this is safe (nothing will change)."
                : `${expirePreview.count} active subscription${expirePreview.count === 1 ? " is" : "s are"} past their end date and have received every tiffin in their plan. They will be marked Expired.`
        }
        confirmLabel={expirePreview.count ? `Expire ${expirePreview.count}` : "Run sweep"}
        variant="accent"
        onConfirm={runExpireSweep}
        loading={expireRunning}
        testId="subscription-expire-sweep-dialog"
      />
    </div>
  );
}
